import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import { Button, Text, Small, Panel, H3, H4, Badge, Box } from '@bigcommerce/big-design';
import { AddIcon, DeleteIcon, FileDownloadIcon, EditIcon, ReceiptIcon, AssignmentIcon, ChevronRightIcon, ChevronLeftIcon, VisibilityIcon, ContentCopyIcon } from '@bigcommerce/big-design-icons';

import { purchaseOrdersIndex, downloadOrderPdf } from '../../redux/actions/purchaseOrders';
import { suppliersIndex } from '../../redux/actions/suppliers';

const RejectedIndex = ({ payload, loadPurchaseOrders, loadSuppliers, purchaseOrders, suppliers, download, downloadUrl }) => {
  const [downloadSubmitted, setDownloadSubmitted] = useState(false);

  const [page, setPage] = useState(0);

  useEffect(() => {
    loadNextPurchaseOrders();
    loadSuppliers(payload);
  }, []);

  useEffect(() => {
    if(downloadUrl && downloadSubmitted) { 
      setDownloadSubmitted(false); 
      setTimeout(() => {
        let win = window.open(downloadUrl, '_blank');
        win.focus();
      }, 0);
    }
  }, [downloadUrl]);

  const loadPreviousPurchaseOrders = () => {
    let previousContinuationToken = purchaseOrders.purchaseOrdersSearchStack[page - 2];
    loadPurchaseOrders(payload, previousContinuationToken, (page - 1));

    setPage(page - 1);
  }

  const loadNextPurchaseOrders = () => {
    loadPurchaseOrders(payload, purchaseOrders.purchaseOrdersSearchStack[page], (page + 1));
    setPage(page + 1);
  }

  const downloadPdf = (purchaseOrderId) => {
    setDownloadSubmitted(true);

    let options = {
      poNum: true,
      invoiceNum: true,
      deliveryDate: true,
      paymentExpectedDate: true,
      adjustedPrice: true,
      estimatedShippingPrice: true,
      taxColumn: true
    }

    download(payload, purchaseOrderId, options);
  }

  const supplierData = (supplierId) => {
    let supplier = suppliers.suppliers.find(supplier => supplier.id === supplierId);

    if(supplier) {
      return (
        <>
          <strong>Supplier</strong>: { supplier.supplier_name }
          <br/>
          { supplier.account_number &&
            <>
              <strong>Account Number</strong>: { supplier.account_number }
              <br/>
            </>
          }
          { supplier.contact_email &&
            <>
              <strong>Contact Email</strong>: { supplier.contact_email }
              <br/>
            </>
          }
          { supplier.contact_phone &&
            <>
              <strong>Contact Phone</strong>: { supplier.contact_phone }
              <br/>
            </>
          }
        </>
      )
    }

    return(
      <>
        <Text><strong>Supplier</strong>: Supplier no longer exists</Text>
      </>
    )
  }

  const description = () => {
    return(
      <> 
        <Text>From this page, you can view rejected purchase orders. These are purchase orders that have been rejected, or partially fulfilled and then rejected.</Text>
        <Text>This data is stored for 30 days before being deleted.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#070808' helpLink="bigcommerce/manage-purchase-orders/" />
      <SettingsPage title="Purchase Orders" description={description()}>

        <Panel>
          <H3>Rejected Purchase Orders</H3>
          <div className="flex-container">
            <div className="flex-1">
              <Button
                onClick={() => loadPreviousPurchaseOrders()}
                disabled={page === 1}
                iconLeft={<ChevronLeftIcon />}
                variant="secondary"
              >
                Previous Purchase Orders
              </Button>

              <Button
                onClick={() => loadNextPurchaseOrders()}
                disabled={purchaseOrders.lastPage}
                iconRight={<ChevronRightIcon />}
                variant="secondary"
              >
                Next Purchase Orders
              </Button>
            </div>
          </div>
          <br/>

          { purchaseOrders.isFetching &&
            <LoadingSpinner statusText="Loading Purchase Orders" />
          }

          { purchaseOrders.purchaseOrders.length === 0 && !purchaseOrders.isFetching &&
            <Box
              backgroundColor="warning10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <div className="text-center">
                <Text>No rejected purchase orders. When you have rejected purchase orders, you'll see them in a list here.</Text>
              </div>
            </Box>
          }

          { purchaseOrders.purchaseOrders.map(purchaseOrder =>
            <>
              <H4>
                {purchaseOrder.invoice_number || purchaseOrder.id}
                <span className="left-15">
                  <Link to={`/purchase_order/copy/${purchaseOrder.id}?status=rejected`}>
                    <Button
                      iconOnly={<ContentCopyIcon />}
                      variant="secondary"
                    />
                  </Link>
                </span>
              </H4>
              
              <div className="flex-container space-between">
                <div className="flex1 left-15">
                  { purchaseOrder.supplier_order_number &&
                    <>
                      <strong>Purchase Order Number</strong>: { purchaseOrder.supplier_order_number }
                      <br/>
                    </>
                  }
                  { purchaseOrder.supplier_id && suppliers.status === 'success' &&
                    <>
                      {supplierData(purchaseOrder.supplier_id)}
                    </>
                  }
                  { purchaseOrder.supplier_id && suppliers.isFetching &&
                    <LoadingSpinner size="xxSmall" statusText="Loading Suppliers" />
                  }

                </div>

                <div className="flex1 left-15">
                  <strong>PO Status</strong>: <Badge label={ purchaseOrder.status } variant={ purchaseOrder.status === 'fulfilled' ? "success" : (purchaseOrder.status === 'draft' ? "warning" : "secondary") } /><br/>
                  <strong>Type</strong>: <Badge label={ purchaseOrder.ship_to_type } variant={ "secondary" } /><br/>
                  <strong>Shipment Expected</strong>: <Badge label={ (new Date(purchaseOrder.shipment_expected_at)).toISOString().split('T')[0] } variant={ ( (new Date(purchaseOrder.shipment_expected_at)) > (new Date()) ) ? "secondary" : "warning" } />
                </div>

                <div className="flex1 left-15">
                  <strong>Payment status</strong>: <Badge label={ purchaseOrder.payment_status } variant={ purchaseOrder.payment_status === 'open' ? "warning" : "success" } />
                  <br/>
                  <strong>Payment due</strong>: <Badge label={ (new Date(purchaseOrder.payment_due_at)).toISOString().split('T')[0] } variant={ ( (new Date(purchaseOrder.payment_due_at)) > (new Date()) ) ? "secondary" : "warning" } />
                </div>
                
                <div className="flex1 left-15 flex-container flex-column">
                  <Button
                    variant="subtle"
                    onClick={() => downloadPdf(purchaseOrder.id)}
                    disabled={downloadSubmitted}
                    isLoading={downloadSubmitted}
                    iconLeft={<FileDownloadIcon />}
                  >
                    Download PDF
                  </Button>
                  <br/>
                </div>
              </div>
              <hr/>
            </>
          )}

        </Panel>

      </SettingsPage>
    </>
  )

}

const mapStateToProps = state => ({
  purchaseOrders: state.purchaseOrders,
  suppliers: state.suppliers,
  downloadUrl: state.purchaseOrders.downloadUrl,
});

const mapDispatchToProps = (dispatch) => ({
  loadPurchaseOrders: (payload, continuationToken, page) => dispatch(purchaseOrdersIndex(payload, 'rejected', continuationToken, page)),
  loadSuppliers: (payload) => dispatch(suppliersIndex(payload)),
  download: (payload, purchaseOrderId, options) => dispatch(downloadOrderPdf(payload, 'rejected', purchaseOrderId, options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RejectedIndex);